import { useEffect, useRef } from 'react';
import { useUpdateNodeInternals } from '@xyflow/react';

/**
 * React Flow caches handle bounds per handle id and only re-measures on node
 * resize. Dynamic-socket steps (approval escalations, switch cases, …) can
 * rename a handle id without changing the node's size, leaving the store
 * pointing at a handle that no longer exists — drags from the renamed socket
 * then silently fail. Force a re-measure whenever the set of socket ids
 * changes.
 *
 * Skips mount: React Flow measures new nodes itself, and a forced re-measure
 * per node (each its own frame + layout + store update) froze the page for
 * over a second when a large graph mounted.
 */
export function useRemeasureOnSocketChange(
  nodeId: string,
  socketIdsKey: string,
) {
  const updateNodeInternals = useUpdateNodeInternals();
  const measuredSocketIdsKey = useRef(socketIdsKey);
  useEffect(() => {
    if (measuredSocketIdsKey.current === socketIdsKey) return;
    measuredSocketIdsKey.current = socketIdsKey;
    updateNodeInternals(nodeId);
  }, [nodeId, socketIdsKey, updateNodeInternals]);
}
